import {host} from "./apiRoutes";

/**
 * Live messages over a plain WebSocket (the server runs on Cloudflare Workers, which can't host
 * socket.io). Keeps the small part of the socket.io API the app uses: on(event, fn) and emit().
 * Reconnects automatically and sends a ping every 30s so idle connections stay open.
 */
class RealtimeSocket {
    constructor(token) {
        this.token = token;
        this.listeners = new Map();
        this.closed = false;
        this.retryDelay = 1000;
        this.connect();
    }

    connect() {
        const base = host || window.location.origin;
        const url = base.replace(/^http/, "ws") + "/ws?token=" + encodeURIComponent(this.token);
        const ws = new WebSocket(url);
        this.ws = ws;

        ws.onopen = () => {
            this.retryDelay = 1000;
            this.pingTimer = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send("ping"), 30000);
        };
        ws.onmessage = (e) => {
            if (e.data === "pong") return;
            try {
                const {event, data} = JSON.parse(e.data);
                (this.listeners.get(event) || []).forEach((fn) => fn(data));
            } catch {
                // ignore malformed messages
            }
        };
        ws.onclose = () => {
            clearInterval(this.pingTimer);
            if (this.closed) return;
            setTimeout(() => !this.closed && this.connect(), this.retryDelay);
            this.retryDelay = Math.min(this.retryDelay * 2, 30000);
        };
    }

    on(event, fn) {
        if (!this.listeners.has(event)) this.listeners.set(event, []);
        this.listeners.get(event).push(fn);
    }

    off(event, fn) {
        this.listeners.set(event, (this.listeners.get(event) || []).filter((f) => f !== fn));
    }

    // The server identifies the user from the token, so "add-user" needs no message
    emit() {
    }

    close() {
        this.closed = true;
        clearInterval(this.pingTimer);
        this.ws.close();
    }
}

let current = null;

/** One connection per logged-in token; reused when the user is reloaded. */
export function connectRealtime(token) {
    if (current && current.token === token && !current.closed) return current;
    if (current) current.close();
    current = new RealtimeSocket(token);
    return current;
}

export function disconnectRealtime() {
    if (current) current.close();
    current = null;
}
